import React, { useState, useEffect, useMemo } from 'react';
import ResearchLayout from '../../components/ResearchLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import AIAssessmentViewer from '../../components/AIAssessmentViewer';
import { PatientMeasurementsDisplay } from '../../components/MeasurementDisplay';
import useUnitPreferences from '../../hooks/useUnitPreferences';

// Clean Case Details Component
const CaseDetailsCard = ({ caseId, compact = false, squeezed = false }) => {
  const { isInternationalSystem } = useUnitPreferences();
  const [caseDetails, setCaseDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseDetails = async () => {
      if (!caseId) return;

      try {
        setLoading(true);
        const response = await api.get(`/cases/${caseId}`);
        if (response.data && response.data.case) {
          const caseData = response.data.case;

          // Ensure aiResponseData is present - parse from aiResponse if needed
          if (!caseData.aiResponseData && caseData.aiResponse) {
            try {
              caseData.aiResponseData = JSON.parse(caseData.aiResponse);
            } catch (e) {
              console.warn('Failed to parse aiResponse:', e);
            }
          }

          setCaseDetails(caseData);
        }
      } catch (error) {
        console.error('Error fetching case details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseDetails();
  }, [caseId]);

  if (loading) {
    return (
      <div className="text-center py-2">
        <div className="spinner-border spinner-border-sm text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!caseDetails) {
    return (
      <div className="text-center py-2">
        <i className="fas fa-exclamation-triangle text-warning fa-lg"></i>
        <p className="text-muted small mt-1">Unable to load case details</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="case-summary">
        <div className="row g-1 mb-1">
          <div className="col-6">
            <small className="text-muted">Case ID</small>
            <div className="fw-bold">{String(caseDetails.id || '').replace(/^0+/, '')}</div>
          </div>
          {caseDetails.score && (
            <div className="col-6">
              <small className="text-muted">Health Score</small>
              <div className={`fw-bold ${caseDetails.score >= 70 ? 'text-success' : caseDetails.score >= 50 ? 'text-warning' : 'text-danger'}`}>
                {caseDetails.score}
              </div>
            </div>
          )}
        </div>
        <div className="row g-1">
          {caseDetails.age && (
            <div className="col-4">
              <small className="text-muted">Age</small>
              <div className="small">{caseDetails.age}y</div>
            </div>
          )}
          {caseDetails.gender && (
            <div className="col-4">
              <small className="text-muted">Gender</small>
              <div className="small">{caseDetails.gender}</div>
            </div>
          )}
          {caseDetails.createdAt && (
            <div className="col-4">
              <small className="text-muted">Date</small>
              <div className="small">{new Date(caseDetails.createdAt).toLocaleDateString()}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const sectionMargin = squeezed ? 'mb-2' : 'mb-3';
  const headingMargin = squeezed ? 'mb-1' : 'mb-2';
  const rowGap = squeezed ? 'g-1' : 'g-2';
  const padding = squeezed ? 'p-1' : 'p-2';

  return (
    <div className="case-details-full">
      {/* Case Header */}
      <div className="mb-3 text-center">
        <h5 className="text-primary mb-1">
          <i className="fas fa-user-injured me-2"></i>
          Case {String(caseDetails.id || '').replace(/^0+/, '')}
        </h5>
        <div className="d-flex justify-content-center gap-2 mb-2">
          {caseDetails.score && (
            <div className={`badge fs-6 ${caseDetails.score >= 70 ? 'bg-success' : caseDetails.score >= 50 ? 'bg-warning' : 'bg-danger'}`}>
              Score: {caseDetails.score}/100
            </div>
          )}
          <div className="badge bg-info">
            {caseDetails.assessmentCompletionPercentage ? `${caseDetails.assessmentCompletionPercentage}%` : 'N/A'} Complete
          </div>
        </div>
      </div>

      {/* 2x4 Grid Layout */}
      <div className="row g-2">
        {/* Row 1, Column 1: Basic Info */}
        <div className="col-3">
          <div className="card h-100 border-success">
            <div className="card-header bg-success text-white text-center py-1">
              <small><i className="fas fa-id-card me-1"></i>Basic Info</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Age:</small>
                <div className="fw-bold small">{caseDetails.age ? `${caseDetails.age}y` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Gender:</small>
                <div className="fw-bold small">{caseDetails.gender || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 1, Column 2: Physical Measurements */}
        <div className="col-3">
          <div className="card h-100 border-primary">
            <div className="card-header bg-primary text-white text-center py-1">
              <small><i className="fas fa-weight me-1"></i>Physical</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Weight:</small>
                <div className="fw-bold small">{caseDetails.weight ? `${caseDetails.weight} kg` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Height:</small>
                <div className="fw-bold small">{caseDetails.height ? `${caseDetails.height} cm` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Waist:</small>
                <div className="fw-bold small">{caseDetails.waist ? `${caseDetails.waist} cm` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Hip:</small>
                <div className="fw-bold small">{caseDetails.hip ? `${caseDetails.hip} cm` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">BMI:</small>
                <div className="fw-bold small">
                  {caseDetails.weight && caseDetails.height ?
                    (caseDetails.weight / ((caseDetails.height / 100) ** 2)).toFixed(1) : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 1, Column 3: Vital Signs */}
        <div className="col-3">
          <div className="card h-100 border-danger">
            <div className="card-header bg-danger text-white text-center py-1">
              <small><i className="fas fa-heartbeat me-1"></i>Vitals</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Systolic BP:</small>
                <div className="fw-bold small">{caseDetails.systolicBloodPressure || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Diastolic BP:</small>
                <div className="fw-bold small">{caseDetails.diastolicBloodPressure || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Heart Rate:</small>
                <div className="fw-bold small">{caseDetails.restingHeartRate ? `${caseDetails.restingHeartRate} bpm` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Blood Glucose:</small>
                <div className="fw-bold small">{caseDetails.bloodGlucose ? `${caseDetails.bloodGlucose} ${caseDetails.bloodGlucoseUnit || 'mg/dL'}` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Oxygen Sat:</small>
                <div className="fw-bold small">{caseDetails.oxygenSaturationLevel ? `${caseDetails.oxygenSaturationLevel}%` : 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 1, Column 4: Lifestyle */}
        <div className="col-3">
          <div className="card h-100 border-warning">
            <div className="card-header bg-warning text-dark text-center py-1">
              <small><i className="fas fa-leaf me-1"></i>Lifestyle</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Smoking:</small>
                <div className="text-end">
                  <div className="fw-bold small">{caseDetails.isSmoker !== null ? (caseDetails.isSmoker ? 'Yes' : 'No') : 'N/A'}</div>
                  {caseDetails.cigarettesPerDay && <div className="small text-muted">{caseDetails.cigarettesPerDay}/day</div>}
                  {caseDetails.yearsSmoked && <div className="small text-muted">{caseDetails.yearsSmoked} yrs</div>}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Alcohol:</small>
                <div className="text-end">
                  <div className="fw-bold small">{caseDetails.isDrinker !== null ? (caseDetails.isDrinker ? 'Yes' : 'No') : 'N/A'}</div>
                  {caseDetails.bottlesPerDay && <div className="small text-muted">{caseDetails.bottlesPerDay}/day</div>}
                  {caseDetails.drinkingDaysPerWeek && <div className="small text-muted">{caseDetails.drinkingDaysPerWeek}/week</div>}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Activity:</small>
                <div className="fw-bold small">{caseDetails.activeDailyLifestyleLevel || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Sleep:</small>
                <div className="fw-bold small">{caseDetails.wellSleepLevel || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2, Column 1: Recent Symptoms */}
        <div className="col-3">
          <div className="card h-100 border-info">
            <div className="card-header bg-info text-white text-center py-1">
              <small><i className="fas fa-exclamation-triangle me-1"></i>Symptoms</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Pain:</small>
                <div className="fw-bold small">{caseDetails.lastMonthPain !== null && caseDetails.lastMonthPain !== undefined ? caseDetails.lastMonthPain : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Vision:</small>
                <div className="fw-bold small">{caseDetails.lastMonthDiminishedVision !== null && caseDetails.lastMonthDiminishedVision !== undefined ? caseDetails.lastMonthDiminishedVision : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Hearing:</small>
                <div className="fw-bold small">{caseDetails.lastMonthDiminishedHearing !== null && caseDetails.lastMonthDiminishedHearing !== undefined ? caseDetails.lastMonthDiminishedHearing : 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2, Column 2: Medical History */}
        <div className="col-3">
          <div className="card h-100 border-secondary">
            <div className="card-header bg-secondary text-white text-center py-1">
              <small><i className="fas fa-notes-medical me-1"></i>Medical</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Chronic Disease:</small>
                <div className="fw-bold small">{caseDetails.diagnosedChronicDisease || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Family History:</small>
                <div className="fw-bold small">{caseDetails.familyMemberDiagnosedChronicDisease || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Education:</small>
                <div className="fw-bold small">
                  {caseDetails.highestEducation === "Master's Degree or Above" || caseDetails.highestEducation === '6' || caseDetails.highestEducation === 6 ? "Master's Degree or Above" :
                   caseDetails.highestEducation === "Bachelor's Degree (4+ years of College)" || caseDetails.highestEducation === '5' || caseDetails.highestEducation === 5 ? "Bachelor's Degree (4+ years of College)" :
                   caseDetails.highestEducation === "Associate's Degree (2+ years of College)" || caseDetails.highestEducation === '4' || caseDetails.highestEducation === 4 ? "Associate's Degree (2+ years of College)" :
                   caseDetails.highestEducation === "High School" || caseDetails.highestEducation === '3' || caseDetails.highestEducation === 3 ? "High School" :
                   caseDetails.highestEducation === "Primary School" || caseDetails.highestEducation === '2' || caseDetails.highestEducation === 2 ? "Primary School" :
                   caseDetails.highestEducation === "Elementary School" || caseDetails.highestEducation === '1' || caseDetails.highestEducation === 1 ? "Elementary School" :
                   caseDetails.highestEducation || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2, Column 3: Mental Health */}
        <div className="col-3">
          <div className="card h-100 border-success">
            <div className="card-header bg-success text-white text-center py-1">
              <small><i className="fas fa-brain me-1"></i>Mental</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Stress:</small>
                <div className="fw-bold small">
                  {caseDetails.stressFrequency === 'Never' || caseDetails.stressFrequency === '0' || caseDetails.stressFrequency === 0 ? 'Never' :
                   caseDetails.stressFrequency === 'Sometimes' || caseDetails.stressFrequency === 'Rare' || caseDetails.stressFrequency === '1' || caseDetails.stressFrequency === 1 ? 'Sometimes' :
                   caseDetails.stressFrequency === 'Many times' || caseDetails.stressFrequency === '2' || caseDetails.stressFrequency === 2 ? 'Many times' :
                   caseDetails.stressFrequency === 'Always' || caseDetails.stressFrequency === 'Often' || caseDetails.stressFrequency === 'Very Often' || caseDetails.stressFrequency === '3' || caseDetails.stressFrequency === 3 ? 'Always' :
                   caseDetails.stressFrequency || 'N/A'}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Mood:</small>
                <div className="fw-bold small">
                  {caseDetails.mood === 'Calm' || caseDetails.mood === '1' || caseDetails.mood === 1 ? 'Calm' :
                   caseDetails.mood === 'Somewhat Calm' || caseDetails.mood === '2' || caseDetails.mood === 2 ? 'Somewhat Calm' :
                   caseDetails.mood === 'Neutral' || caseDetails.mood === '3' || caseDetails.mood === 3 ? 'Neutral' :
                   caseDetails.mood === 'Somewhat Anxious' || caseDetails.mood === '4' || caseDetails.mood === 4 ? 'Somewhat Anxious' :
                   caseDetails.mood === 'Anxious' || caseDetails.mood === '5' || caseDetails.mood === 5 ? 'Anxious' :
                   caseDetails.mood || 'N/A'}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Appetite:</small>
                <div className="fw-bold small">
                  {caseDetails.lossOfInterestAppetiteFrequency === 'Never' || caseDetails.lossOfInterestAppetiteFrequency === '0' || caseDetails.lossOfInterestAppetiteFrequency === 0 ? 'Never' :
                   caseDetails.lossOfInterestAppetiteFrequency === 'Sometimes' || caseDetails.lossOfInterestAppetiteFrequency === 'Rare' || caseDetails.lossOfInterestAppetiteFrequency === '1' || caseDetails.lossOfInterestAppetiteFrequency === 1 ? 'Sometimes' :
                   caseDetails.lossOfInterestAppetiteFrequency === 'Many times' || caseDetails.lossOfInterestAppetiteFrequency === '2' || caseDetails.lossOfInterestAppetiteFrequency === 2 ? 'Many times' :
                   caseDetails.lossOfInterestAppetiteFrequency === 'Always' || caseDetails.lossOfInterestAppetiteFrequency === 'Often' || caseDetails.lossOfInterestAppetiteFrequency === 'Very Often' || caseDetails.lossOfInterestAppetiteFrequency === '3' || caseDetails.lossOfInterestAppetiteFrequency === 3 ? 'Always' :
                   caseDetails.lossOfInterestAppetiteFrequency || 'N/A'}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Fatigue:</small>
                <div className="fw-bold small">
                  {caseDetails.fatigueFrequency === 'Never' || caseDetails.fatigueFrequency === '0' || caseDetails.fatigueFrequency === 0 ? 'Never' :
                   caseDetails.fatigueFrequency === 'Sometimes' || caseDetails.fatigueFrequency === 'Rare' || caseDetails.fatigueFrequency === '1' || caseDetails.fatigueFrequency === 1 ? 'Sometimes' :
                   caseDetails.fatigueFrequency === 'Many times' || caseDetails.fatigueFrequency === '2' || caseDetails.fatigueFrequency === 2 ? 'Many times' :
                   caseDetails.fatigueFrequency === 'Always' || caseDetails.fatigueFrequency === 'Often' || caseDetails.fatigueFrequency === 'Very Often' || caseDetails.fatigueFrequency === '3' || caseDetails.fatigueFrequency === 3 ? 'Always' :
                   caseDetails.fatigueFrequency || 'N/A'}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Mental Health:</small>
                <div className="fw-bold small">{caseDetails.mentalHealthPrediction || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2, Column 4: AI Assessment */}
        <div className="col-3">
          <div className="card h-100 border-primary">
            <div className="card-header bg-primary text-white text-center py-1">
              <small><i className="fas fa-robot me-1"></i>AI Assessment</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Physical:</small>
                <div className="fw-bold small">{caseDetails.physicalHealthPrediction || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Clinical:</small>
                <div className="fw-bold small">{caseDetails.clinicalHealthPrediction || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Lifestyle:</small>
                <div className="fw-bold small">{caseDetails.lifestylePrediction || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Evaluation Details - Full Width */}
      {caseDetails.aiResponseData && Object.keys(caseDetails.aiResponseData).length > 0 && (
        <div className="mt-3">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light">
              <h6 className="text-success mb-0">
                <i className="fas fa-robot me-1"></i>
                AI Evaluation Details
              </h6>
            </div>
            <div className="card-body p-2">
              <AIAssessmentViewer data={caseDetails.aiResponseData} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const QualityOfAnswersReport = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [filters, setFilters] = useState({
    case_id: '',
    doctor_id: '',
    protocol_id: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [viewMode, setViewMode] = useState('cases'); // 'cases' or 'overview'
  const [activeView, setActiveView] = useState('cases'); // For navigation tabs
  const [selectedCaseDetails, setSelectedCaseDetails] = useState(null);
  const [loadingCaseDetails, setLoadingCaseDetails] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [expandedDoctors, setExpandedDoctors] = useState(new Set());
  const [expandedCases, setExpandedCases] = useState(new Set());
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [caseDetailsCache, setCaseDetailsCache] = useState(new Map()); // Cache for case details
  const [doctorFilter, setDoctorFilter] = useState('active'); // 'all', 'active', 'inactive'
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    total_cases: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
    next_page: null,
    prev_page: null
  });
  
  // Load data on component mount, when filters, pagination, or doctorFilter change
  useEffect(() => {
    loadData();
  }, [filters, pagination.page, doctorFilter]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Build query parameters from filters and pagination
      const queryParams = new URLSearchParams();
      if (filters.case_id) queryParams.append('case_id', filters.case_id);
      if (filters.doctor_id) queryParams.append('doctor_id', filters.doctor_id);
      if (filters.protocol_id) queryParams.append('protocol_id', filters.protocol_id);
      // Add doctor status filter
      if (doctorFilter !== 'all') queryParams.append('doctor_status', doctorFilter);
      
      // Add pagination parameters
      queryParams.append('page', pagination.page.toString());
      queryParams.append('per_page', pagination.per_page.toString());

      const response = await api.get(`/reports/quality-of-answers?${queryParams.toString()}`);
      if (response.data && response.data.success) {
        const casesData = response.data.cases || [];
        setCases(casesData);
        
        // Update pagination state
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }

        // Pre-load case details for all cases to speed up modal loading
        const uniqueCaseIds = [...new Set(casesData.map(caseItem => caseItem.case_id))];
        const cachePromises = uniqueCaseIds.map(async (caseId) => {
          if (!caseDetailsCache.has(caseId)) {
            try {
              const caseResponse = await api.get(`/cases/${caseId}`);
              if (caseResponse.data && caseResponse.data.case) {
                setCaseDetailsCache(prev => new Map(prev).set(caseId, caseResponse.data.case));
              }
            } catch (error) {
              console.warn(`Failed to pre-load case details for case ${caseId}:`, error);
            }
          }
        });

        // Load case details in background (don't await)
        Promise.all(cachePromises).catch(error =>
          console.warn('Some case details failed to load:', error)
        );
      } else {
        setCases([]);
      }
    } catch (error) {
      console.error('Error loading quality of answers data:', error);
      setError('Failed to load quality of answers report. ' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  };

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoadingFilters(true);
        // Fetch all doctors (including inactive) when we need to filter
        const [doctorsResponse, protocolsResponse] = await Promise.all([
          api.get('/doctors?include_inactive=true'),
          api.get('/protocols?page=1&per_page=1000')
        ]);

        if (doctorsResponse.data?.doctors) {
          let filteredDoctors = doctorsResponse.data.doctors;
          
          // Apply filter based on doctorFilter state
          // Also consider soft-deleted (email prefix) as inactive
          if (doctorFilter === 'active') {
            filteredDoctors = filteredDoctors.filter(
              doctor => doctor.is_active !== false && !doctor.email?.startsWith('deleted_') && !doctor.email?.startsWith('delete_delete_')
            );
          } else if (doctorFilter === 'inactive') {
            filteredDoctors = filteredDoctors.filter(
              doctor => doctor.is_active === false || doctor.email?.startsWith('deleted_') || doctor.email?.startsWith('delete_delete_')
            );
          }
          // 'all' shows all doctors
          
          setDoctors(filteredDoctors);
        }
        if (protocolsResponse.data?.protocols) {
          setProtocols(protocolsResponse.data.protocols);
        }
      } catch (error) {
        console.error('Error loading filter options:', error);
      } finally {
        setLoadingFilters(false);
      }
    };

    loadFilterOptions();
  }, [doctorFilter]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // If selecting a doctor, check if they are inactive and auto-switch to 'all' filter
    if (name === 'doctor_id' && value) {
      const selectedDoctor = doctors.find(d => d.id === parseInt(value));
      if (selectedDoctor && selectedDoctor.is_active === false) {
        setDoctorFilter('all');
      }
    }
    
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Reset to first page when search changes
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      case_id: '',
      doctor_id: '',
      protocol_id: ''
    });
    setSearchTerm('');
    // Reset to first page when clearing filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Pagination functions
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page }));
    }
  };

  const nextPage = () => {
    if (pagination.has_next) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const prevPage = () => {
    if (pagination.has_prev) {
      setPagination(prev => ({ ...prev, page: prev.page - 1 }));
    }
  };

  // Refresh function for the button
  const loadQualityOfAnswersData = () => {
    // Reset to first page when refreshing
    setPagination(prev => ({ ...prev, page: 1 }));
    loadData();
  };

  // Memoize filteredCases to prevent unnecessary recalculations
  const filteredCases = useMemo(() => {
    return cases.filter(caseItem => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      const caseIdMatch = String(caseItem.case_id || '').replace(/^0+/, '').toLowerCase().includes(searchLower);
      const customerIdMatch = String(caseItem.customer_id || '').toLowerCase().includes(searchLower);
      const caseScoreMatch = caseItem.case_score?.toString().includes(searchLower);

      const answerMatch = caseItem.answers.some(answer =>
        answer.question_text?.toLowerCase().includes(searchLower) ||
        answer.answer_content?.toLowerCase().includes(searchLower) ||
        answer.doctor_name?.toLowerCase().includes(searchLower) ||
        answer.protocol_name?.toLowerCase().includes(searchLower)
      );

      return caseIdMatch || customerIdMatch || caseScoreMatch || answerMatch;
    });
  }, [cases, searchTerm]);

  // Generate doctor data for doctor view
  useEffect(() => {
    const doctorMap = {};
    filteredCases.forEach(caseItem => {
      caseItem.answers.forEach(answer => {
        const doctorId = answer.doctor_id;
        if (!doctorMap[doctorId]) {
          doctorMap[doctorId] = {
            doctor_id: doctorId,
            doctor_name: answer.doctor_name,
            protocols: {},
            total_cases: 0,
            total_answers: 0
          };
        }

        const protocolId = answer.protocol_id;
        if (!doctorMap[doctorId].protocols[protocolId]) {
          doctorMap[doctorId].protocols[protocolId] = {
            protocol_id: protocolId,
            protocol_name: answer.protocol_name,
            cases: {}
          };
        }

        const caseId = caseItem.case_id;
        if (!doctorMap[doctorId].protocols[protocolId].cases[caseId]) {
          doctorMap[doctorId].protocols[protocolId].cases[caseId] = {
            case_id: caseId,
            case_score: caseItem.case_score,
            answers: []
          };
        }

        doctorMap[doctorId].protocols[protocolId].cases[caseId].answers.push(answer);
        doctorMap[doctorId].total_answers++;
      });
    });

    // Convert to array and calculate total cases
    const doctorArray = Object.values(doctorMap).map(doctor => {
      doctor.total_cases = Object.values(doctor.protocols)
        .reduce((acc, protocol) => acc + Object.keys(protocol.cases).length, 0);
      return doctor;
    });

    setFilteredDoctors(doctorArray);
  }, [filteredCases]);

  // Toggle functions for expand/collapse
  const toggleDoctorExpansion = (doctorId) => {
    const newExpanded = new Set(expandedDoctors);
    if (newExpanded.has(doctorId)) {
      newExpanded.delete(doctorId);
    } else {
      newExpanded.add(doctorId);
    }
    setExpandedDoctors(newExpanded);
  };

  const toggleCaseExpansion = (caseId) => {
    const newExpanded = new Set(expandedCases);
    if (newExpanded.has(caseId)) {
      newExpanded.delete(caseId);
    } else {
      newExpanded.add(caseId);
    }
    setExpandedCases(newExpanded);
  };

  // Handle case selection for modal
  const handleCaseClick = (caseItem) => {
    setSelectedCase(caseItem);
    setShowCaseModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowCaseModal(false);
    setSelectedCase(null);
  };

  // Handle view more button click
  const handleViewMore = (answers, caseItem) => {
    setSelectedAnswer(answers); // Now passing all answers
    setSelectedCase(caseItem); // Set selectedCase to trigger case details loading
    setShowCaseModal(true);
  };

  // Load case details for detailed modal (use cache if available)
  useEffect(() => {
    const loadCaseDetails = async () => {
      if (selectedCase?.case_id) {
        // Check if we have cached case details
        const cachedCase = caseDetailsCache.get(selectedCase.case_id);
        if (cachedCase) {
          setSelectedCaseDetails(cachedCase);
          setLoadingCaseDetails(false);
          return;
        }

        // If not cached, load from API
        try {
          setLoadingCaseDetails(true);
          const response = await api.get(`/cases/${selectedCase.case_id}`);
          if (response.data && response.data.case) {
            const caseData = response.data.case;
            setSelectedCaseDetails(caseData);
            // Cache for future use
            setCaseDetailsCache(prev => new Map(prev).set(selectedCase.case_id, caseData));
          }
        } catch (error) {
          console.error('Error loading case details:', error);
        } finally {
          setLoadingCaseDetails(false);
        }
      }
    };

    loadCaseDetails();
  }, [selectedCase, caseDetailsCache]);

  return (
    <ResearchLayout pageTitle="Quality of Answers Report">
      <div className="container-fluid">
        {/* Back Button */}
        <div className="mb-3">
          <button
            className="btn btn-outline-secondary"
            onClick={() => window.history.back()}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Reports
          </button>
        </div>

        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div></div>
          <button
            className="btn btn-outline-primary"
            onClick={loadQualityOfAnswersData}
            disabled={loading}
          >
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* View Navigation Tabs */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-dark border-0">
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeView === 'cases' ? 'active' : ''}`}
                  onClick={() => setActiveView('cases')}
                >
                  <i className="fas fa-user-injured me-2"></i>
                  Case View
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeView === 'doctors' ? 'active' : ''}`}
                  onClick={() => setActiveView('doctors')}
                >
                  <i className="fas fa-user-md me-2"></i>
                  Doctor View
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Filters Section */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label htmlFor="case_id" className="form-label">Filter by Case ID</label>
                <input
                  type="text"
                  id="case_id"
                  name="case_id"
                  className="form-control"
                  placeholder="Enter case ID..."
                  value={filters.case_id}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-md-3">
                <label htmlFor="doctor_id" className="form-label">Filter by Doctor</label>
                <select
                  id="doctor_id"
                  name="doctor_id"
                  className="form-select"
                  value={filters.doctor_id}
                  onChange={handleFilterChange}
                  disabled={loadingFilters}
                >
                  <option value="">All Doctors</option>
                  {doctors.map(doctor => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name} {doctor.is_active === false ? '(Inactive)' : ''}
                    </option>
                  ))}
                </select>
                {user && (user.role === 'program_admin' || user.role === 'medical_admin' || (typeof user.role === 'string' && (user.role.toLowerCase().includes('program_admin') || user.role.toLowerCase().includes('medical_admin')))) && (
                  <div className="mt-2">
                    <label className="form-label small">Doctor Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={doctorFilter}
                      onChange={(e) => setDoctorFilter(e.target.value)}
                    >
                      <option value="active">Active Only</option>
                      <option value="inactive">Inactive Only</option>
                      <option value="all">All Doctors</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="col-md-3">
                <label htmlFor="protocol_id" className="form-label">Filter by Protocol</label>
                <select
                  id="protocol_id"
                  name="protocol_id"
                  className="form-select"
                  value={filters.protocol_id}
                  onChange={handleFilterChange}
                  disabled={loadingFilters}
                >
                  <option value="">All Protocols</option>
                  {protocols.map(protocol => (
                    <option key={protocol.id} value={protocol.id}>
                      {protocol.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label htmlFor="search" className="form-label">Search</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    id="search"
                    className="form-control"
                    placeholder="Search answers, doctors..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  {(searchTerm || filters.case_id || filters.doctor_id || filters.protocol_id) && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={clearFilters}
                      title="Clear all filters"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading quality of answers report...</span>
            </div>
            <p className="mt-2">Loading quality of answers report...</p>
          </div>
        ) : activeView === 'doctors' ? (
          // Doctor View - Cleaner layout
          <>
            <div className="row">
              {filteredDoctors.length > 0 ? (
                filteredDoctors.map((doctorData) => {
                  const isExpanded = expandedDoctors.has(doctorData.doctor_id);
                  const allCases = Object.values(doctorData.protocols).flatMap(protocol =>
                    Object.values(protocol.cases)
                  );
                  const displayedCases = isExpanded ? allCases : allCases.slice(0, 1);

                  return (
                    <div key={doctorData.doctor_id} className="col-12 mb-4">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">
                            <i className="fas fa-user-md me-2"></i>
                            Dr. {doctorData.doctor_name}
                          </h5>
                          <div className="d-flex align-items-center gap-3">
                            <span className="badge bg-light text-dark">
                              {doctorData.total_cases} Case{doctorData.total_cases !== 1 ? 's' : ''}
                            </span>
                            <span className="badge bg-light text-dark">
                              {doctorData.total_answers} Answer{doctorData.total_answers !== 1 ? 's' : ''}
                            </span>
                            {doctorData.total_cases > 1 && (
                              <button
                                className="btn btn-sm btn-light"
                                onClick={() => toggleDoctorExpansion(doctorData.doctor_id)}
                              >
                                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} me-1`}></i>
                                {isExpanded ? 'Show Less' : `Show More (${doctorData.total_cases - 1} more)`}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="card-body">
                          {/* Display cases */}
                          <div className="row">
                            {displayedCases.map((caseData) => (
                              <div key={caseData.case_id} className="col-lg-6 mb-3">
                                <div className="card border">
                                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">
                                      <i className="fas fa-user-injured text-success me-2"></i>
                                      Case {String(caseData.case_id || '').replace(/^0+/, '')}
                                    </h6>
                                    <div className="d-flex align-items-center gap-2">
                                      {caseData.case_score && (
                                        <span className={`badge ${caseData.case_score >= 70 ? 'bg-success' : caseData.case_score >= 50 ? 'bg-warning' : 'bg-danger'}`}>
                                          Score: {caseData.case_score}
                                        </span>
                                      )}
                                      <button
                                        className="btn btn-sm btn-outline-info"
                                        onClick={() => toggleCaseExpansion(caseData.case_id)}
                                      >
                                        <i className="fas fa-expand me-1"></i>
                                        Details
                                      </button>
                                    </div>
                                  </div>
                                  <div className="card-body">
                                    {/* Case Summary */}
                                    <div className="row mb-3">
                                      <div className="col-6">
                                        <small className="text-muted d-block">Total Answers</small>
                                        <span className="badge bg-primary">{caseData.answers.length}</span>
                                      </div>
                                      <div className="col-6">
                                        <small className="text-muted d-block">Doctor Performance</small>
                                        <div className="fw-bold text-success">Good</div>
                                      </div>
                                    </div>

                                    {/* Answers */}
                                    <div className="answers-section">
                                      <h6 className="text-primary mb-2">
                                        <i className="fas fa-comments me-2"></i>
                                        Answers ({caseData.answers.length})
                                      </h6>
                                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {caseData.answers.slice(0, 3).map((answer, index) => (
                                          <div key={answer.answer_id} className="mb-2">
                                            <div className="bg-light p-2 rounded small">
                                              <strong className="text-primary">Q{index + 1}:</strong> {answer.question_text?.substring(0, 60)}...
                                            </div>
                                            <div className="bg-white p-2 rounded border small">
                                              <strong className="text-success">A{index + 1}:</strong> {answer.answer_content?.substring(0, 60)}...
                                            </div>
                                          </div>
                                        ))}
                                        {caseData.answers.length > 3 && (
                                          <div className="text-center">
                                            <small className="text-muted">...and {caseData.answers.length - 3} more answers</small>
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        className="btn btn-sm btn-outline-primary mt-2"
                                        onClick={() => handleViewMore(caseData.answers, { case_id: caseData.case_id })}
                                      >
                                        <i className="fas fa-eye me-1"></i>
                                        View Full Details
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-12">
                  <div className="text-center py-5">
                    <i className="fas fa-user-md fa-3x text-muted mb-3"></i>
                    <h4>No Doctors Found</h4>
                    <p className="text-muted">
                      No doctors with answers are available for the quality of answers report.
                    </p>
                    {(searchTerm || filters.case_id || filters.doctor_id || filters.protocol_id) && cases.length > 0 && (
                      <button className="btn btn-outline-primary" onClick={clearFilters}>
                        <i className="fas fa-times me-2"></i>
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {!loading && pagination.total_pages > 0 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-3">
                        <span className="text-muted">
                          Showing <strong>{filteredDoctors.length}</strong> of <strong>{pagination.total_cases}</strong> doctors
                          {pagination.total_pages > 0 && (
                            <span> (Page <strong>{pagination.page}</strong> of <strong>{pagination.total_pages}</strong>)</span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <nav aria-label="Doctors pagination">
                        <ul className="pagination pagination-sm justify-content-end mb-0">
                          <li className={`page-item ${!pagination.has_prev ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={prevPage}
                              disabled={!pagination.has_prev}
                            >
                              <i className="fas fa-chevron-left"></i>
                              Previous
                            </button>
                          </li>
                          
                          {/* Page numbers */}
                          {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                            let pageNum;
                            if (pagination.total_pages <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.page >= pagination.total_pages - 2) {
                              pageNum = pagination.total_pages - 4 + i;
                            } else {
                              pageNum = pagination.page - 2 + i;
                            }
                            
                            return (
                              <li key={pageNum} className={`page-item ${pageNum === pagination.page ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => goToPage(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          })}
                          
                          <li className={`page-item ${!pagination.has_next ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={nextPage}
                              disabled={!pagination.has_next}
                            >
                              Next
                              <i className="fas fa-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          // Case View - Improved layout
          <>
            <div className="row">
              {filteredCases.length > 0 ? (
                filteredCases.map((caseItem) => (
                  <div key={caseItem.case_id} className="col-lg-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                          <i className="fas fa-user-injured text-primary me-2"></i>
                          Case {String(caseItem.case_id || '').replace(/^0+/, '')}
                        </h5>
                        <div className="d-flex gap-2">
                          {caseItem.case_score && (
                            <span className={`badge ${caseItem.case_score >= 70 ? 'bg-success' : caseItem.case_score >= 50 ? 'bg-warning' : 'bg-danger'}`}>
                              Score: {caseItem.case_score}
                            </span>
                          )}
                          <span className="badge bg-info">
                            {caseItem.answers.length} Answer{caseItem.answers.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      <div className="card-body">
                        {/* Case Summary */}
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <small className="text-muted">Assessment Completion</small>
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className="progress-bar bg-success"
                                style={{ width: `${caseItem.assessment_completion_percentage || 0}%` }}
                              ></div>
                            </div>
                            <small className="text-muted">{caseItem.assessment_completion_percentage || 0}% Complete</small>
                          </div>
                          <div className="col-md-6">
                            <small className="text-muted">Created</small>
                            <div>{caseItem.created_at ? new Date(caseItem.created_at).toLocaleDateString() : 'N/A'}</div>
                          </div>
                        </div>

                        {/* Case Details Summary */}
                        <div className="mb-3">
                          <CaseDetailsCard caseId={caseItem.case_id} compact={true} />
                        </div>

                        {/* Answers Overview */}
                        <div className="answers-section">
                          <h6 className="text-primary mb-3">
                            <i className="fas fa-comments me-2"></i>
                            Doctor Answers Overview
                          </h6>
                          <div className="row">
                            {caseItem.answers.slice(0, 2).map((answer, index) => (
                              <div key={answer.answer_id} className="col-12 mb-3">
                                <div className="card border">
                                  <div className="card-body">
                                    <div className="bg-light p-2 rounded small mb-2">
                                      <strong className="text-primary">Q{index + 1}:</strong> {answer.question_text}
                                    </div>
                                    <div className="bg-white p-2 rounded border small mb-2">
                                      <strong className="text-success">A{index + 1}:</strong> {answer.answer_content}
                                    </div>
                                    <div className="row align-items-center">
                                      <div className="col-md-8">
                                        <small className="text-muted">Protocol:</small>
                                        <span className="badge bg-light text-dark ms-1">{answer.protocol_name}</span>
                                      </div>
                                      <div className="col-md-4 text-end">
                                        <div className="mb-2">
                                          <small className="text-muted">Answered by:</small>
                                          <div className="fw-bold text-primary">{answer.doctor_name}</div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {caseItem.answers.length > 2 && (
                              <div className="col-12 text-center">
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => handleViewMore(caseItem.answers, { case_id: caseItem.case_id })}
                                >
                                  <i className="fas fa-search-plus me-1"></i>
                                  View All {caseItem.answers.length} Answers
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12">
                  <div className="text-center py-5">
                    <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                    <h4>No Cases Found</h4>
                    <p className="text-muted">
                      {cases.length === 0 ?
                        'No cases with answers are available for the quality of answers report.' :
                        'No cases match your current filters. Try adjusting your search criteria.'}
                    </p>
                    {(searchTerm || filters.case_id || filters.doctor_id || filters.protocol_id) && cases.length > 0 && (
                      <button className="btn btn-outline-primary" onClick={clearFilters}>
                        <i className="fas fa-times me-2"></i>
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Pagination Controls */}
            {!loading && pagination.total_pages > 0 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-3">
                        <span className="text-muted">
                          Showing <strong>{cases.length}</strong> of <strong>{pagination.total_cases}</strong> cases
                          {pagination.total_pages > 0 && (
                            <span> (Page <strong>{pagination.page}</strong> of <strong>{pagination.total_pages}</strong>)</span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <nav aria-label="Cases pagination">
                        <ul className="pagination pagination-sm justify-content-end mb-0">
                          <li className={`page-item ${!pagination.has_prev ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={prevPage}
                              disabled={!pagination.has_prev}
                            >
                              <i className="fas fa-chevron-left"></i>
                              Previous
                            </button>
                          </li>
                           
                          {/* Page numbers */}
                          {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                            let pageNum;
                            if (pagination.total_pages <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.page >= pagination.total_pages - 2) {
                              pageNum = pagination.total_pages - 4 + i;
                            } else {
                              pageNum = pagination.page - 2 + i;
                            }
                             
                            return (
                              <li key={pageNum} className={`page-item ${pageNum === pagination.page ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => goToPage(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          })}
                           
                          <li className={`page-item ${!pagination.has_next ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={nextPage}
                              disabled={!pagination.has_next}
                            >
                              Next
                              <i className="fas fa-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Enhanced Answer Detail Modal */}
        {showCaseModal && selectedAnswer && Array.isArray(selectedAnswer) && (
          <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                {(() => {
                  const uniqueProtocols = [...new Set(selectedAnswer.map(a => a.protocol_name))];
                  const uniqueDoctors = [...new Set(selectedAnswer.map(a => a.doctor_name))];
                  return (
                    <div className="modal-header bg-success text-white">
                      <div>
                        <h5 className="modal-title mb-1">
                          <i className="fas fa-search-plus me-2"></i>
                          Complete Case Analysis - All {selectedAnswer.length} Answers
                        </h5>
                        <div className="small">
                          <strong>Protocols:</strong> {uniqueProtocols.join(', ')} |
                          <strong className="ms-2">Doctors:</strong> {uniqueDoctors.join(', ')}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        onClick={closeModal}
                      ></button>
                    </div>
                  );
                })()}
                <div className="modal-body p-0">
                  <div className="row g-0">
                    {/* Left Side - All Answers List */}
                    <div className="col-lg-5">
                      <div className="p-3" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                        <h6 className="text-primary mb-3">
                          <i className="fas fa-comments me-2"></i>
                          All Questions & Answers ({selectedAnswer.length})
                        </h6>

                        <div className="answers-list">
                          {selectedAnswer.map((answer, index) => (
                            <div key={answer.answer_id} className="mb-3">
                              <div className="card">
                                <div className="card-header bg-light">
                                  <h6 className="mb-0 text-primary">
                                    <i className="fas fa-question-circle me-2"></i>
                                    Q{index + 1}: {answer.question_text}
                                  </h6>
                                </div>
                                <div className="card-body">
                                  <p className="card-text"><strong>A{index + 1}:</strong> {answer.answer_content}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Right Side - Case Details */}
                    <div className="col-lg-7">
                      <div className="p-0" style={{ overflowY: 'auto', maxHeight: '60vh' }}>
                        <h6 className="text-success mb-2">
                          <i className="fas fa-user-injured me-2"></i>
                          Complete Case Assessment
                        </h6>

                        {loadingCaseDetails ? (
                          <div className="text-center py-3">
                            <div className="spinner-border text-success" role="status">
                              <span className="visually-hidden">Loading case details...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading case details...</p>
                          </div>
                        ) : selectedCaseDetails ? (
                          <div className="case-details-full">
                            {/* Render case details directly since we have them loaded */}
                            <CaseDetailsCard caseId={selectedCase.case_id} compact={false} squeezed={true} />
                          </div>
                        ) : (
                          <div className="text-center py-3">
                            <i className="fas fa-exclamation-triangle text-warning fa-2x mb-2"></i>
                            <p className="text-muted">Unable to load case details</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                  >
                    <i className="fas fa-times me-1"></i>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default QualityOfAnswersReport;